import React, { useState, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import Spinner, { InlineSpinner } from "components/Spinner";
import OpenQueryReminderModal from "components/OpenQueryReminderModal";

function OpenQueryRemindersContainer() {
  const [reminders, setReminders] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [reminderToEdit, setReminderToEdit] = useState(null);

  // ---- Auth gate ----
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setReminders(null);            // ★ clear stale data
          setReminderToEdit(null);       // ★ close any edit context
          setShowReminderModal(false);   // ★ ensure modal is closed
          setDeleteInProgress(null);     // ★ reset button state
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const ensureAuthed = async () => {
    await Auth.currentAuthenticatedUser();
  };

  // ---- Fetch reminders (only when authed) ----
  const inFlight = useRef(false);
  useEffect(() => {
    if (!ready) return;
    if (inFlight.current) return;
    inFlight.current = true;

    const getReminders = async () => {
      try {
        await ensureAuthed();
        const res = await API.get("OpenQueryAPI", `/reminders`);
        setReminders(res?.Items || []);
      } catch (err) {
        console.error("error fetching reminders", err);
      } finally {
        inFlight.current = false;
      }
    };

    getReminders();
  }, [ready]);

  const handleReminderEdit = (item) => {
    setReminderToEdit(item);
    setShowReminderModal(true);
  };

  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setReminderToEdit(null); // optional tidy-up
  };

  const handleReminderDelete = async (id) => {
    try {
      if (!ready) return;
      await ensureAuthed();

      setDeleteInProgress(id);
      await API.del("OpenQueryAPI", `/reminders/${id}`);
      const res = await API.get("OpenQueryAPI", `/reminders`);
      setReminders(res?.Items || []);
      setDeleteInProgress(null);
    } catch (err) {
      setDeleteInProgress(null);
      setErrorMsg("error deleting reminder");
      console.error("error deleting reminder", err);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header text-light font-weight-bold container-fluid">
          <div className="row">
            <div className="col-12">Reminders</div>
          </div>
        </div>
        <div className="card-body p-0">
          <table className="table table-striped table-responsive" style={{ padding: 0 }}>
            <thead>
              <tr>
                <th scope="col">Id</th>
                <th scope="col">Type</th>
                <th scope="col">Value</th>
                <th scope="col">Description</th>
                <th scope="col">Edit</th>
                <th scope="col">Delete</th>
              </tr>
            </thead>
            <tbody>
              {!ready && (
                <tr>
                  <td colSpan="6" className="text-center">
                    <Spinner />
                  </td>
                </tr>
              )}
              {ready && !reminders && (
                <tr>
                  <td colSpan="6" className="text-center">
                    <Spinner />
                  </td>
                </tr>
              )}
              {ready &&
                reminders &&
                reminders.map((item) => (
                  <tr key={item.REMINDER_ID}>
                    <th scope="row">{item.REMINDER_ID || ""}</th>
                    <td>{item.DATE_TYPE || ""}</td>
                    <td>{item.DATE_VALUE || ""}</td>
                    <td>{item.REMINDER_DESCRIPTION || ""}</td>
                    <td>
                      <button className="btn btn-secondary" onClick={() => handleReminderEdit(item)}>
                        Edit
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReminderDelete(item.REMINDER_ID)}
                        disabled={deleteInProgress === item.REMINDER_ID}
                      >
                        {deleteInProgress === item.REMINDER_ID && <InlineSpinner />}
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              {ready && reminders && reminders.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No reminders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={!!errorMsg} onHide={() => setErrorMsg("")}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorMsg}</p>
        </Modal.Body>
      </Modal>

      <OpenQueryReminderModal
        title="Manage Reminder"
        reminder={reminderToEdit}
        setReminders={setReminders}
        showReminderModal={showReminderModal}
        handleCloseReminderModal={handleCloseReminderModal}
      />
    </>
  );
}

export default OpenQueryRemindersContainer;
